import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  FormControlLabel,
  Chip,
  Rating,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  VideoLibrary as VideoIcon,
  Campaign as CampaignIcon,
  AttachMoney as MoneyIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

export default function Profile({ user, userData }) {
  const [editMode, setEditMode] = useState(false);
  const [profileData, setProfileData] = useState({
    displayName: '',
    email: '',
    phone: '',
    location: '',
    bio: '',
    website: '',
    specialties: [],
    paymentMethods: []
  });
  const [stats, setStats] = useState({
    rating: 0,
    totalEarnings: 0,
    totalSubmissions: 0,
    totalCampaigns: 0,
    approvalRate: 0,
    joinDate: null
  });
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    marketingEmails: false,
    profileVisibility: 'public'
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editField, setEditField] = useState('');

  useEffect(() => {
    if (user && userData) {
      setProfileData({
        displayName: user.displayName || userData.name || '',
        email: user.email || '',
        phone: userData.phone || '',
        location: userData.location || '',
        bio: userData.bio || '',
        website: userData.website || '',
        specialties: userData.specialties || ['Gaming', 'Sports'],
        paymentMethods: userData.paymentMethods || []
      });

      setStats({
        rating: userData.rating || 4.5,
        totalEarnings: userData.totalEarnings || 2890.75,
        totalSubmissions: userData.totalSubmissions || 47,
        totalCampaigns: userData.totalCampaigns || 5,
        approvalRate: userData.approvalRate || 87.2,
        joinDate: userData.createdAt?.toDate() || new Date()
      });
    }
  }, [user, userData]);

  const handleSave = () => {
    // Save profile changes to Firebase
    setEditMode(false);
  };

  const handleSettingChange = (setting) => (event) => {
    setSettings(prev => ({
      ...prev,
      [setting]: event.target.checked
    }));
  };

  const ProfileStat = ({ icon, label, value, color = 'primary' }) => (
    <Card>
      <CardContent sx={{ textAlign: 'center' }}>
        <Box sx={{ color: `${color}.main`, mb: 1 }}>
          {icon}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: `${color}.main` }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Profile
        </Typography>
        <Button
          variant={editMode ? 'outlined' : 'contained'}
          startIcon={editMode ? <CancelIcon /> : <EditIcon />}
          onClick={() => setEditMode(!editMode)}
          sx={editMode ? {} : { bgcolor: '#ff5252', '&:hover': { bgcolor: '#ff3939' } }}
        >
          {editMode ? 'Cancel' : 'Edit Profile'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Avatar
              src={user?.photoURL}
              sx={{ 
                width: 120, 
                height: 120, 
                margin: '0 auto 16px',
                bgcolor: '#ff5252',
                fontSize: '3rem'
              }}
            >
              {profileData.displayName?.charAt(0) || 'U'}
            </Avatar>
            
            {editMode ? (
              <TextField
                fullWidth
                value={profileData.displayName}
                onChange={(e) => setProfileData(prev => ({ ...prev, displayName: e.target.value }))}
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
              />
            ) : (
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                {profileData.displayName}
              </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
              <Rating value={stats.rating} precision={0.1} readOnly />
              <Typography variant="body2" sx={{ ml: 1 }}>
                ({stats.rating})
              </Typography>
            </Box>

            <Chip 
              label={userData?.role || 'User'} 
              color="primary" 
              sx={{ mb: 2 }}
            />

            <Typography variant="body2" color="text.secondary">
              Member since {stats.joinDate?.toLocaleDateString()}
            </Typography>

            {editMode && (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                sx={{ 
                  mt: 2, 
                  bgcolor: '#ff5252', 
                  '&:hover': { bgcolor: '#ff3939' } 
                }}
              >
                Save Changes
              </Button>
            )}
          </Paper>

          {/* Quick Stats */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Performance
            </Typography>
            <List>
              <ListItem sx={{ px: 0 }}>
                <ListItemText 
                  primary="Approval Rate" 
                  secondary={
                    <Box>
                      <Typography variant="body2">{stats.approvalRate}%</Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={stats.approvalRate} 
                        color="success"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  }
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemText 
                  primary="Total Earnings" 
                  secondary={`$${stats.totalEarnings.toLocaleString()}`}
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemText 
                  primary="Submissions" 
                  secondary={stats.totalSubmissions}
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemText 
                  primary="Campaigns Created" 
                  secondary={stats.totalCampaigns}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Stats Overview */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <ProfileStat
                icon={<MoneyIcon fontSize="large" />}
                label="Total Earnings"
                value={`$${stats.totalEarnings.toLocaleString()}`}
                color="success"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ProfileStat
                icon={<VideoIcon fontSize="large" />}
                label="Submissions"
                value={stats.totalSubmissions}
                color="primary"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ProfileStat
                icon={<CampaignIcon fontSize="large" />}
                label="Campaigns"
                value={stats.totalCampaigns}
                color="secondary"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ProfileStat
                icon={<TrendingUpIcon fontSize="large" />}
                label="Success Rate"
                value={`${stats.approvalRate}%`}
                color="info"
              />
            </Grid>
          </Grid>

          {/* Personal Information */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Personal Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={profileData.email}
                      onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                      size="small"
                      disabled
                    />
                  ) : (
                    <Typography>{profileData.email}</Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={profileData.phone}
                      onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                      size="small"
                      placeholder="Phone number"
                    />
                  ) : (
                    <Typography>{profileData.phone || 'Not provided'}</Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={profileData.location}
                      onChange={(e) => setProfileData(prev => ({ ...prev, location: e.target.value }))}
                      size="small"
                      placeholder="Location"
                    />
                  ) : (
                    <Typography>{profileData.location || 'Not specified'}</Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WorkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={profileData.website}
                      onChange={(e) => setProfileData(prev => ({ ...prev, website: e.target.value }))}
                      size="small"
                      placeholder="Website"
                    />
                  ) : (
                    <Typography>{profileData.website || 'Not provided'}</Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                {editMode ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={profileData.bio}
                    onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder="Tell us about yourself..."
                    label="Bio"
                  />
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Bio
                    </Typography>
                    <Typography>
                      {profileData.bio || 'No bio provided yet.'}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* Specialties */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Specialties
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {profileData.specialties.map((specialty, index) => (
                <Chip
                  key={index}
                  label={specialty}
                  color="primary"
                  variant="outlined"
                />
              ))}
              {editMode && (
                <Chip
                  label="+ Add Specialty"
                  color="primary"
                  variant="outlined"
                  onClick={() => setEditDialogOpen(true)}
                />
              )}
            </Box>
          </Paper>

          {/* Account Settings */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Account Settings
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <NotificationsIcon />
                </ListItemIcon>
                <ListItemText primary="Email Notifications" />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailNotifications}
                      onChange={handleSettingChange('emailNotifications')}
                    />
                  }
                  label=""
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <NotificationsIcon />
                </ListItemIcon>
                <ListItemText primary="Push Notifications" />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.pushNotifications}
                      onChange={handleSettingChange('pushNotifications')}
                    />
                  }
                  label=""
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText primary="Marketing Emails" />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.marketingEmails}
                      onChange={handleSettingChange('marketingEmails')}
                    />
                  }
                  label=""
                />
              </ListItem>
            </List>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="outlined" startIcon={<SecurityIcon />}>
                Change Password
              </Button>
              <Button variant="outlined" startIcon={<PaymentIcon />}>
                Payment Methods
              </Button>
              <Button variant="outlined" color="error">
                Delete Account
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
